/**
 * Disbursements list with cursor pagination and a wizard modal for creating
 * a new disbursement. The wizard mirrors PAYOK's 2-step remit flow:
 *
 *   1. Form: pick bank + account number + amount + holder name → /inquiry.
 *   2. Confirm screen: review the PAYOK-confirmed holder name + fees → /create.
 *
 * Both steps are server calls; the modal stores the intermediate inquiry
 * result locally so the user can back out before committing the wallet move.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Collapse,
  Grid,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronRight,
  IconFilter,
  IconFilterOff,
  IconPlus,
  IconRefresh,
  IconChartBar,
  IconShieldLock,
} from '@tabler/icons-react';
import {
  banksApi,
  disbursementsApi,
  type DisbursementFilter,
  type InquiryResult,
} from '../api/disbursements';
import { merchantsApi } from '../api/merchants';
import { mfaApi } from '../api/mfa';
import type { TrxStatus } from '../api/types';
import { formatGmt7, formatIdr, statusColor } from '../lib/format';
import { ApiCallError, describeError } from '../api/client';
import { DataTable } from '../components/DataTable';
import { ListPagination } from '../components/ListPagination';
import { ListSummaryStrip } from '../components/ListSummaryStrip';
import { useSession, usePermissions } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { useTranslation } from '../i18n/LocaleContext';

function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Per-column filter state for disbursements. Mirrors Deposits.tsx FilterDraft
 * shape, minus deposit-only ranges (settleAt, merchantRevenueMin/Max) and
 * plus disbursement-only fields (bankCode multi-select, holderName ILIKE).
 */
type FilterDraft = {
  trxId: string;
  referenceId: string;
  merchantId: string[];
  createdBy: string;
  fromIp: string;
  trxStatus: string[];
  bankCode: string[];
  holderName: string;
  createdAt: [Date | null, Date | null];
  paidAt: [Date | null, Date | null];
  amountMin: number | '';
  amountMax: number | '';
  feeMin: number | '';
  feeMax: number | '';
};

const EMPTY_DRAFT: FilterDraft = {
  trxId: '',
  referenceId: '',
  merchantId: [],
  createdBy: '',
  fromIp: '',
  trxStatus: [],
  bankCode: [],
  holderName: '',
  createdAt: [null, null],
  paidAt: [null, null],
  amountMin: '',
  amountMax: '',
  feeMin: '',
  feeMax: '',
};

function draftToFilter(d: FilterDraft): DisbursementFilter {
  const out: DisbursementFilter = { limit: 25 };
  if (d.trxId.trim()) out.trxId = d.trxId.trim();
  if (d.referenceId.trim()) out.referenceId = d.referenceId.trim();
  if (d.merchantId.length) out.merchantId = d.merchantId;
  if (d.createdBy.trim()) out.createdBy = d.createdBy.trim();
  if (d.fromIp.trim()) out.fromIp = d.fromIp.trim();
  if (d.trxStatus.length) out.trxStatus = d.trxStatus as TrxStatus[];
  if (d.bankCode.length) out.bankCode = d.bankCode;
  if (d.holderName.trim()) out.holderName = d.holderName.trim();
  if (d.createdAt[0]) out.createdAtFrom = toIsoDate(d.createdAt[0]);
  if (d.createdAt[1]) out.createdAtTo = toIsoDate(d.createdAt[1]);
  if (d.paidAt[0]) out.paidAtFrom = toIsoDate(d.paidAt[0]);
  if (d.paidAt[1]) out.paidAtTo = toIsoDate(d.paidAt[1]);
  if (d.amountMin !== '') out.amountMin = d.amountMin;
  if (d.amountMax !== '') out.amountMax = d.amountMax;
  if (d.feeMin !== '') out.feeMin = d.feeMin;
  if (d.feeMax !== '') out.feeMax = d.feeMax;
  return out;
}

function countActiveFilters(d: FilterDraft): number {
  let n = 0;
  if (d.trxId.trim()) n += 1;
  if (d.referenceId.trim()) n += 1;
  if (d.merchantId.length) n += 1;
  if (d.createdBy.trim()) n += 1;
  if (d.fromIp.trim()) n += 1;
  if (d.trxStatus.length) n += 1;
  if (d.bankCode.length) n += 1;
  if (d.holderName.trim()) n += 1;
  if (d.createdAt[0] || d.createdAt[1]) n += 1;
  if (d.paidAt[0] || d.paidAt[1]) n += 1;
  if (d.amountMin !== '' || d.amountMax !== '') n += 1;
  if (d.feeMin !== '' || d.feeMax !== '') n += 1;
  return n;
}

export function DisbursementsPage() {
  const navigate = useNavigate();
  const { state } = useSession();
  const { t } = useTranslation();
  const { has } = usePermissions();
  const isPlatformUser = state.status === 'authenticated' && isPlatform(state.profile);
  // Withdrawals are merchant-side only: internal/platform users (incl. superuser)
  // can never initiate one, so they never see the create action. The withdraw-svc
  // enforces the same rule server-side (internal_withdrawal_forbidden).
  const canCreate = !isPlatformUser && has('disbursements.create');

  // MFA status — only fetched for users who can actually submit disbursements.
  // Used to render the proactive enroll banner below; if MFA is already on,
  // the banner stays hidden. staleTime keeps refetches off the hot path.
  const mfaStatusQuery = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => mfaApi.status(),
    enabled: canCreate,
    staleTime: 30_000,
  });
  const needsMfaEnroll = canCreate && mfaStatusQuery.data?.enabled === false;

  const [filter, setFilter] = useState<DisbursementFilter>({ limit: 25 });
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Overview strip is collapsed by default (toggle in the page header); its
  // summary request only runs while it is open.
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Internal users get a Merchant multi-select; external users are auto-
  // scoped to their own merchantId server-side.
  const merchantsQuery = useQuery({
    queryKey: ['merchants-for-disbursement-filter'],
    queryFn: () => merchantsApi.list({ limit: 100 }),
    enabled: isPlatformUser,
  });

  // Bank list is already imported elsewhere on this page (the wizard). Reuse.
  const banksQuery = useQuery({
    queryKey: ['banks-list'],
    queryFn: () => banksApi.list(),
  });

  const queryFilter: DisbursementFilter = {
    ...filter,
    page,
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['disbursements', queryFilter],
    queryFn: () => disbursementsApi.page(queryFilter),
    retry: 0,
    placeholderData: (prev) => prev,
  });

  const applyFilters = () => {
    setPage(1);
    setFilter(draftToFilter(draft));
  };
  const clearFilters = () => {
    setDraft(EMPTY_DRAFT);
    setPage(1);
    setFilter({ limit: 25 });
  };

  const activeCount = countActiveFilters(draft);
  const setField = <K extends keyof FilterDraft>(k: K, v: FilterDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  // Filtered summary (overview strip): same filter, pagination stripped.
  const summaryQuery = useQuery({
    queryKey: ['disbursements-summary', filter],
    queryFn: () => disbursementsApi.summary(filter),
    enabled: overviewOpen,
    retry: 0,
    placeholderData: (prev) => prev,
  });

  const pagination = (
    <ListPagination
      page={page}
      pageCount={data?.pageCount ?? 1}
      total={data?.total ?? 0}
      limit={filter.limit ?? 25}
      loading={isFetching}
      onPageChange={setPage}
      onLimitChange={(limit) => {
        setPage(1);
        setFilter((f) => ({ ...f, limit }));
      }}
    />
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('disbursements.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('disbursements.subtitle')}
          </Text>
        </div>
        <Group gap="xs">
          <Tooltip label={t('list.toggleOverview')}>
            <ActionIcon
              variant={overviewOpen ? 'filled' : 'default'}
              aria-pressed={overviewOpen}
              aria-label={t('list.toggleOverview')}
              onClick={() => setOverviewOpen((v) => !v)}
            >
              <IconChartBar size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('list.toggleFilters')}>
            <ActionIcon
              variant={filtersOpen ? 'filled' : 'default'}
              aria-pressed={filtersOpen}
              aria-label={t('list.toggleFilters')}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <IconFilter size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('common.refresh')}>
            <ActionIcon variant="default" onClick={() => void refetch()} loading={isFetching}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          {canCreate && (
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={() => setCreateOpen(true)}
              disabled={needsMfaEnroll}
            >
              {t('disbursements.create')}
            </Button>
          )}
        </Group>
      </Group>

      {/* Proactive MFA-enroll banner. Visible only for users that have
          disbursements.create AND have not enrolled MFA yet — they cannot
          submit until they do, so direct them to the Security page up front
          instead of letting them fill the wizard and fail at submit time. */}
      {needsMfaEnroll && (
        <Alert
          color="yellow"
          icon={<IconShieldLock size={18} />}
          variant="light"
          title={t('disbursements.mfaBannerTitle')}
        >
          <Group justify="space-between" align="center" wrap="nowrap" gap="md">
            <Text size="sm" dangerouslySetInnerHTML={{ __html: t('disbursements.mfaBannerBody') }} />
            <Button
              component={Link}
              to="/dashboard/security"
              color="yellow"
              variant="filled"
              leftSection={<IconShieldLock size={14} />}
              style={{ flexShrink: 0 }}
            >
              {t('disbursements.mfaEnrollNow')}
            </Button>
          </Group>
        </Alert>
      )}

      <Collapse in={overviewOpen}>
      <ListSummaryStrip
        summary={summaryQuery.data}
        loading={summaryQuery.isLoading}
        error={summaryQuery.error}
      />
      </Collapse>

      <Card>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap={6}>
              <Text size="sm" fw={600}>{t('disbursements.filters')}</Text>
              {activeCount > 0 && (
                <Badge color="brand" variant="light" size="sm">
                  {t('disbursements.activeFilters', { n: activeCount })}
                </Badge>
              )}
            </Group>
            <Group gap={6}>
              <Button
                size="xs"
                variant="subtle"
                leftSection={filtersOpen ? <IconFilterOff size={14} /> : <IconFilter size={14} />}
                onClick={() => setFiltersOpen((o) => !o)}
              >
                {filtersOpen ? t('common.collapse') : t('common.show')}
              </Button>
            </Group>
          </Group>
          <Collapse in={filtersOpen}>
            <Stack gap="md">
              {/* Identifiers row */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('disbursements.colTrxId')}
                    placeholder={t('disbursements.placeholderTrxId')}
                    value={draft.trxId}
                    onChange={(e) => setField('trxId', e.currentTarget.value)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('disbursements.colMerchantReff')}
                    placeholder={t('disbursements.placeholderReff')}
                    value={draft.referenceId}
                    onChange={(e) => setField('referenceId', e.currentTarget.value)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('disbursements.colCreatedBy')}
                    placeholder={t('disbursements.colCreatedByPartial')}
                    value={draft.createdBy}
                    onChange={(e) => setField('createdBy', e.currentTarget.value)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                  <TextInput
                    label={t('disbursements.colRequestedFrom')}
                    placeholder={t('disbursements.placeholderIp')}
                    value={draft.fromIp}
                    onChange={(e) => setField('fromIp', e.currentTarget.value)}
                  />
                </Grid.Col>
              </Grid>

              {/* Categorical row */}
              <Grid>
                {isPlatformUser && (
                  <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                    <MultiSelect
                      label={t('disbursements.colMerchantName')}
                      placeholder={draft.merchantId.length > 0 ? undefined : t('disbursements.placeholderAllMerchants')}
                      data={(merchantsQuery.data?.rows ?? []).map((m) => ({
                        value: m.id,
                        label: m.name,
                      }))}
                      value={draft.merchantId}
                      onChange={(v) => setField('merchantId', v)}
                      searchable
                      clearable
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={{ base: 12, md: 6, lg: isPlatformUser ? 3 : 4 }}>
                  <MultiSelect
                    label={t('disbursements.colStatus')}
                    placeholder={draft.trxStatus.length > 0 ? undefined : t('disbursements.placeholderAllStatuses')}
                    data={['pending', 'paid', 'failed']}
                    value={draft.trxStatus}
                    onChange={(v) => setField('trxStatus', v)}
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: isPlatformUser ? 3 : 4 }}>
                  <MultiSelect
                    label={t('disbursements.colBank')}
                    placeholder={draft.bankCode.length > 0 ? undefined : t('disbursements.placeholderAllBanks')}
                    data={(banksQuery.data?.rows ?? []).map((b) => ({
                      value: b.orgCode,
                      label: b.orgName,
                    }))}
                    value={draft.bankCode}
                    onChange={(v) => setField('bankCode', v)}
                    searchable
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: isPlatformUser ? 3 : 4 }}>
                  <TextInput
                    label={t('disbursements.colBeneficiary')}
                    placeholder={t('disbursements.placeholderHolder')}
                    value={draft.holderName}
                    onChange={(e) => setField('holderName', e.currentTarget.value)}
                  />
                </Grid.Col>
              </Grid>

              {/* Date ranges */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DatePickerInput
                    type="range"
                    allowSingleDateInRange
                    label={t('disbursements.createdAtLabel')}
                    placeholder={t('disbursements.placeholderDateRange')}
                    value={draft.createdAt}
                    onChange={(v) => setField('createdAt', v as [Date | null, Date | null])}
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DatePickerInput
                    type="range"
                    allowSingleDateInRange
                    label={t('disbursements.paidAtLabel')}
                    placeholder={t('disbursements.placeholderDateRange')}
                    value={draft.paidAt}
                    onChange={(v) => setField('paidAt', v as [Date | null, Date | null])}
                    clearable
                  />
                </Grid.Col>
              </Grid>

              {/* Numeric ranges */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Text size="xs" c="dimmed" fw={600} mb={4}>{t('disbursements.amountLabel')}</Text>
                  <Group grow gap="xs">
                    <NumberInput
                      placeholder={t('disbursements.placeholderMin')}
                      min={0}
                      thousandSeparator=","
                      value={draft.amountMin}
                      onChange={(v) => setField('amountMin', typeof v === 'number' ? v : '')}
                    />
                    <NumberInput
                      placeholder={t('disbursements.placeholderMax')}
                      min={0}
                      thousandSeparator=","
                      value={draft.amountMax}
                      onChange={(v) => setField('amountMax', typeof v === 'number' ? v : '')}
                    />
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Text size="xs" c="dimmed" fw={600} mb={4}>{t('disbursements.feeLabel')}</Text>
                  <Group grow gap="xs">
                    <NumberInput
                      placeholder={t('disbursements.placeholderMin')}
                      min={0}
                      thousandSeparator=","
                      value={draft.feeMin}
                      onChange={(v) => setField('feeMin', typeof v === 'number' ? v : '')}
                    />
                    <NumberInput
                      placeholder={t('disbursements.placeholderMax')}
                      min={0}
                      thousandSeparator=","
                      value={draft.feeMax}
                      onChange={(v) => setField('feeMax', typeof v === 'number' ? v : '')}
                    />
                  </Group>
                </Grid.Col>
              </Grid>

              <Group justify="flex-end">
                <Button variant="default" onClick={clearFilters}>
                  {t('common.reset')}
                </Button>
                <Button onClick={applyFilters}>{t('common.apply')}</Button>
              </Group>
            </Stack>
          </Collapse>
        </Stack>
      </Card>

      {/* overflow visible: the DataTable's sticky proxy scrollbar must not be
          clipped by the Card (Mantine Card is overflow:hidden by default). */}
      <Card p={0} style={{ overflow: 'visible' }}>
        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
            {describeError(error)}
          </Alert>
        )}
        {!isLoading && !error && (
          <DataTable
            tableId="disbursements"
            toolbar={pagination}
            columns={[
              { key: 'trxId', label: t('disbursements.colTrxId'), width: 290, sticky: 'left' },
              { key: 'ref', label: t('disbursements.colMerchantReff'), width: 190 },
              { key: 'merchant', label: t('disbursements.colMerchantName'), width: 190 },
              { key: 'createdAt', label: t('disbursements.colCreatedAt'), width: 150 },
              { key: 'createdBy', label: t('disbursements.colCreatedBy'), width: 210 },
              { key: 'fromIp', label: t('disbursements.colRequestedFrom'), width: 130 },
              { key: 'bank', label: t('disbursements.colBank'), width: 150 },
              { key: 'beneficiary', label: t('disbursements.colBeneficiary'), width: 220 },
              { key: 'status', label: t('disbursements.colStatus'), width: 120 },
              { key: 'paidAt', label: t('disbursements.colPaidAt'), width: 150 },
              { key: 'amount', label: t('disbursements.colAmount'), width: 140, align: 'right' },
              { key: 'fee', label: t('disbursements.colFee'), width: 120, align: 'right' },
              { key: 'revenue', label: t('disbursements.colMerchantRevenue'), width: 160, align: 'right', sticky: 'right' },
              { key: 'action', width: 44, fixed: true, sticky: 'right' },
            ]}
          >
                {data?.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={14}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('disbursements.empty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {data?.rows.map((d) => {
                  // Wallet impact for the merchant on a cashout = -(amount + fee).
                  // We compute this in the UI rather than read backend's stored
                  // `merchant_revenue` because the backend column was originally
                  // populated with the deposit-side convention (amount - fee, a
                  // positive credit) which is wrong for disbursements. Computing
                  // here keeps the display correct without a DB migration.
                  const walletImpactAbs = d.amount + d.fee;
                  return (
                    <Table.Tr
                      key={d.trxId}
                      onClick={() => navigate(`/dashboard/disbursements/${d.trxId}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {d.trxId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.referenceId}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{d.merchantName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{formatGmt7(d.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.createdBy}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace" c="dimmed">{d.fromIp ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="sm">{d.bankName ?? d.bankCode ?? '—'}</Text>
                          <Text size="xs" c="dimmed" ff="monospace">{d.accountNumber ?? '—'}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.holderName ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={statusColor(d.trxStatus)} variant="light">
                          {d.trxStatus}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{d.paidAt ? formatGmt7(d.paidAt) : '—'}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatIdr(d.amount)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c="dimmed">{formatIdr(d.fee)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" fw={500} c="red">
                          {`- ${formatIdr(walletImpactAbs)}`}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <IconChevronRight size={16} />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
          </DataTable>
        )}
        <Group justify="flex-end" p="md">
          {pagination}
        </Group>
      </Card>

      <CreateDisbursementWizard
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          setPage(1);
          void refetch();
        }}
      />
    </Stack>
  );
}

function CreateDisbursementWizard({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [inquiryResult, setInquiryResult] = useState<InquiryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [form, setForm] = useState({
    bankCode: '',
    accountNumber: '',
    holderName: '',
    amount: 50000,
    remark: '',
  });

  const banksQuery = useQuery({
    queryKey: ['banks'],
    queryFn: () => banksApi.list(),
    enabled: opened,
    retry: 0,
  });
  const bankOptions = useMemo(
    () =>
      (banksQuery.data?.rows ?? []).map((b) => ({
        value: b.orgCode,
        label: `${b.orgName} (${b.orgCode})`,
      })),
    [banksQuery.data],
  );

  const inquiryMut = useMutation({
    // inquiry is a bank-account lookup, not a save. Errors are shown inline in
    // the wizard form so the user can fix the account number and retry without
    // losing context, so suppress the global toast on both success and error.
    meta: { successNotification: false, errorNotification: false },
    mutationFn: () =>
      disbursementsApi.inquiry({
        bankCode: form.bankCode,
        accountNumber: form.accountNumber,
        holderName: form.holderName,
        amount: form.amount,
      }),
    onSuccess: (result) => {
      setError(null);
      setInquiryResult(result);
      // Replace user-supplied holder name with PAYOK-confirmed value.
      if (result.verifiedHolderName) {
        setForm((f) => ({ ...f, holderName: result.verifiedHolderName ?? f.holderName }));
      }
      setStep('confirm');
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        setError(`${err.code}: ${err.message}`);
      } else {
        setError((err as Error).message);
      }
    },
  });

  const createMut = useMutation({
    meta: {
      successKey: 'notify.disbursement.create.ok',
      errorKey: 'notify.disbursement.create.fail',
    },
    mutationFn: () => {
      if (!inquiryResult) throw new Error('inquiry result missing');
      // Customer (cardHolderInfo) is derived server-side from the inquiry-
      // verified holderName — the merchant user doesn't supply it. The
      // upstream-provider compliance block is opaque to merchant UX.
      return disbursementsApi.create({
        trxId: inquiryResult.trxId,
        password,
        mfaCode: mfaCode.trim(),
        remark: form.remark,
      });
    },
    onSuccess: () => {
      reset();
      onCreated();
    },
    onError: (err) => {
      // Field-level validation affordances: keep these inline on their inputs
      // so the user can fix password / MFA and retry without losing the wizard
      // state. The generic error toast is handled globally via meta above.
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid_password') {
          setPwError(t('disbursements.errInvalidPassword'));
          return;
        }
        if (err.code === 'invalid_mfa_code') {
          setMfaError(t('disbursements.errInvalidMfa'));
          return;
        }
      }
    },
  });

  function reset() {
    setStep('form');
    setInquiryResult(null);
    setError(null);
    setPassword('');
    setPwError(null);
    setMfaCode('');
    setMfaError(null);
  }
  function handleClose() {
    if (inquiryMut.isPending || createMut.isPending) return;
    reset();
    onClose();
  }

  const formValid =
    form.bankCode.length > 0 &&
    form.accountNumber.length >= 4 &&
    form.holderName.length > 0 &&
    form.amount >= 10000;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={step === 'form' ? t('disbursements.wizardTitleForm') : t('disbursements.wizardTitleConfirm')}
      size="lg"
      closeOnClickOutside={!inquiryMut.isPending && !createMut.isPending}
    >
      {step === 'form' && (
        <Stack>
          <Text size="sm" c="dimmed">
            {t('disbursements.wizardStep1')}
          </Text>
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {error}
            </Alert>
          )}
          <Select
            label={t('disbursements.wizardBankLabel')}
            placeholder={banksQuery.isLoading ? t('disbursements.wizardBankLoading') : t('disbursements.wizardBankPlaceholder')}
            data={bankOptions}
            value={form.bankCode}
            onChange={(v) => setForm((f) => ({ ...f, bankCode: v ?? '' }))}
            searchable
            required
          />
          <Group grow>
            <TextInput
              label={t('disbursements.wizardAccountNumberLabel')}
              placeholder={t('disbursements.wizardAccountNumberPlaceholder')}
              value={form.accountNumber}
              onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.currentTarget.value }))}
              required
            />
            <TextInput
              label={t('disbursements.wizardHolderNameLabel')}
              placeholder={t('disbursements.wizardHolderNamePlaceholder')}
              value={form.holderName}
              onChange={(e) => setForm((f) => ({ ...f, holderName: e.currentTarget.value }))}
              required
            />
          </Group>
          <NumberInput
            label={t('disbursements.wizardAmountLabel')}
            placeholder={t('disbursements.wizardAmountPlaceholder')}
            min={10000}
            max={50_000_000}
            value={form.amount}
            onChange={(v) => setForm((f) => ({ ...f, amount: typeof v === 'number' ? v : 0 }))}
            thousandSeparator=","
            required
          />
          <TextInput
            label={t('disbursements.wizardRemarkLabel')}
            placeholder={t('disbursements.wizardRemarkPlaceholder')}
            value={form.remark}
            onChange={(e) => setForm((f) => ({ ...f, remark: e.currentTarget.value }))}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose} disabled={inquiryMut.isPending}>
              {t('disbursements.wizardCancel')}
            </Button>
            <Button
              onClick={() => inquiryMut.mutate()}
              loading={inquiryMut.isPending}
              disabled={!formValid}
            >
              {t('disbursements.wizardValidateAccount')}
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'confirm' && inquiryResult && (
        <Stack>
          <Alert color="blue" icon={<IconCheck size={16} />}>
            {t('disbursements.wizardConfirmAlert')}
          </Alert>
          <Field label={t('disbursements.wizardFieldBank')}>{inquiryResult.bankName} ({inquiryResult.bankCode})</Field>
          <Field label={t('disbursements.wizardFieldAccountNumber')}>
            <Code>{inquiryResult.accountNumber}</Code>
          </Field>
          <Field label={t('disbursements.wizardFieldVerifiedHolder')}>
            <Text fw={600}>{inquiryResult.verifiedHolderName ?? form.holderName}</Text>
          </Field>
          <Field label={t('disbursements.wizardFieldAmount')}>{formatIdr(inquiryResult.amount)}</Field>
          <Field label={t('disbursements.wizardFieldFee')}>{formatIdr(inquiryResult.fee)}</Field>
          <Field label={t('disbursements.wizardFieldTotalDebit')}>
            <Text fw={700} size="lg">{formatIdr(inquiryResult.totalDebit)}</Text>
          </Field>
          <Field label={t('disbursements.wizardFieldInquiryToken')}>
            <Code style={{ fontSize: 11 }}>{inquiryResult.inquiryToken}</Code>
          </Field>
          <PasswordInput
            label={t('disbursements.wizardPasswordLabel')}
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.currentTarget.value);
              if (pwError) setPwError(null);
            }}
            error={pwError ?? undefined}
            description={t('disbursements.wizardPasswordDesc')}
            disabled={createMut.isPending}
            required
          />
          <TextInput
            label={t('disbursements.wizardMfaLabel')}
            placeholder={t('disbursements.wizardMfaPlaceholder')}
            value={mfaCode}
            onChange={(e) => {
              setMfaCode(e.currentTarget.value);
              if (mfaError) setMfaError(null);
            }}
            error={mfaError ?? undefined}
            description={t('disbursements.wizardMfaDesc')}
            disabled={createMut.isPending}
            required
          />
          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={() => setStep('form')} disabled={createMut.isPending}>
              {t('disbursements.wizardBackEdit')}
            </Button>
            <Group gap="xs">
              <Button variant="default" onClick={handleClose} disabled={createMut.isPending}>
                {t('disbursements.wizardCancel')}
              </Button>
              <Button
                onClick={() => createMut.mutate()}
                loading={createMut.isPending}
                disabled={password.length === 0 || mfaCode.trim().length < 6}
                color="red"
              >
                {t('disbursements.wizardSubmit')}
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <div>{children}</div>
    </Group>
  );
}
